import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { confirmUrlPost, getUrlFacebook, getUrlTwitter, validateParticipation } from "@/services/make-or-get-url-post/make-or-get-url-post";
import { useSupportedSocialMediaContext } from "@/contexts/supported-social-media-context/useSupportedSocialMediaContext";
import { amplitudeAddTrack } from "@/utils/amplitud/event-track";
import { getError } from "@/utils/get-error/get-error";
import { CustomErrorProcessType, UrlProcessType, UsernameProcessType } from "../components/text-node-with-process/text-node-with-process-type";
import { ConfirmUrlPostBody, GetUrlPost, ValidateParticipantBody, ValidateParticipantTargetType } from "@/services/make-or-get-url-post/make-or-get-url-post-types";
import useFetch from "@/hook/useFetch/useFetch";
import { useUserContext } from "@/contexts/user/useUserContext";
import { AccountTargetSupportedSocialMedia } from "@/types/account-targert-supported-social-media/account-targert-supported-social-media";

const REGEX_SOCIAL_MEDIA_PROFILE = [
  {
    name: 'FACEBOOK',
    regex: /https?:\/\/(?:www\.)?(?:facebook|fb)\.com\/profile\.php\?id=\d+(&\S*)?/i
  },
  {
    name: 'TWITTER',
    regex: /https?:\/\/(?:www\.)?(?:twitter|x)\.com\/[A-Za-z0-9_.-]+/i
  },
  {
    name: 'INSTAGRAM',
    regex: /https?:\/\/(?:www\.)?instagram\.com\/[A-Za-z0-9_.-]+/i
  },
  {
    name: 'TIKTOK',
    regex: /https?:\/\/(?:www\.)?tiktok\.com\/[A-Za-z0-9_.-]+/i
  }
];

export type HookObjectivePostType = {
  isSaveUsername: boolean;
  username: string;
  handleChangeUsername: (value: string) => void;
  isSavePost: boolean;
  urlPost: string; 
  handleChangeUrlPost: (value: string) => void;
  processUsername: UsernameProcessType;
  handleChangeProcessUsername: (value: UsernameProcessType) => void;
  processUrl: UrlProcessType;
  handleChangeProcessUrl: (value: UrlProcessType) => void;
  loading: boolean;
  error: string;
  confirm: boolean;
  postSaved: string,
  socialMediaSelected?: AccountTargetSupportedSocialMedia;
  countErrorSavePost: number;
  handleSubmitUsername: (url: string) => Promise<void>;
  handleSubmitUrlPost: (url: string) => Promise<void>;
  handleConfirm: () => Promise<void>;
  handleCancelConfirm: () => void;
}

const SOCIAL_GETS_FUNCTIONS: Record<string, (params: GetUrlPost) => Promise<object>> = {
  'FACEBOOK': getUrlFacebook,
  'TWITTER': getUrlTwitter  
}

const useObjectivePost = (
  idSM: number, 
  campaignId: number, 
  missionId: number,
  getProgress: ()=> Promise<void>,
  post_url?: string
) =>{
  const { isConnect } = useUserContext();
  const { socialMedia } = useSupportedSocialMediaContext();
  const socialMediaSelected = socialMedia?.find(sm => sm?.id === idSM) ?? undefined;
  const nameSocialMedia = String(socialMediaSelected?.name).toUpperCase();
  const getFunction = SOCIAL_GETS_FUNCTIONS?.[nameSocialMedia] ?? undefined;
  const [username, setUsername] = useState<string>('');
  const [isSaveUsername, setIsSaveUsername] = useState<boolean>(false);
  const [urlPost, setUrlPost] = useState<string>('');
  const [isSavePost, setIsSavePost] = useState<boolean>(false);
  const [confirm, setConfirm] = useState<boolean>(false);
  const [processUsername, setProcessUsername] = useState<CustomErrorProcessType>('POST_VISIBLE');
  const [processUrl, setProcessUrl] = useState<UrlProcessType>('SAVE_URL');
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string>('');
  const [countErrorSavePost, setCountErrorSavePost] = useState<number>(0);
  const [postSaved, setPostSaved] = useState<string>('');

  const handleChangeUsername = useCallback((value: string) => setUsername(value), []);
  const handleChangeUrlPost = useCallback((value: string) => setUrlPost(value), []);
  const handleChangeProcessUsername = useCallback((value: CustomErrorProcessType) => setProcessUsername(value), []);
  const handleChangeProcessUrl = useCallback((value: UrlProcessType) => setProcessUrl(value), []);
  const handleChangeError = useCallback((value: string) => setError(value), []);

  const { 
    data, 
    loading: loadingGetUrlPost, 
    startFetch: validateUrlPost
  } = useFetch(getFunction, 
    { campaign_id: campaignId, mission_id: missionId }, 
    { autoFetch: false }
  );

  const determineTypeInput = useCallback((urlPost: string): ValidateParticipantTargetType => {
    const socialMediaProfile = REGEX_SOCIAL_MEDIA_PROFILE.find(({ regex }) => regex.test(urlPost));
    
    if (socialMediaProfile) {
      return 'user_url';
    }

    if (urlPost?.startsWith('https://')) {
      return 'post_url';
    }

    return 'username';
  }, []);

  const handleSubmitUsername = useCallback(async() =>{
    try {
      setIsSaveUsername(false);
      setError('');
      setLoading(true);
      setProcessUsername('SEARCH_POST');
      const body: ValidateParticipantBody = {
        campaign_id: campaignId, 
        mission_id: missionId,
        media: String(nameSocialMedia).toLowerCase(),
        target: {
          type: determineTypeInput(username),
          value: username
        }
      }
      const res = await validateParticipation?.(body);
      const { user_posts = [] } = res || {};
      if(Array.isArray(user_posts) && user_posts?.length === 0 || !user_posts){
        setProcessUsername('USERNAME_NOT_FOUND');
        return;
      }
      setPostSaved(Array.isArray(user_posts) && user_posts?.length > 0 ? user_posts[0] : '');
      setIsSavePost(true);
      setIsSaveUsername(true);
    } catch (error) {
      if (error instanceof Error) {
        const errorMessage = getError(error);
        handleChangeError(errorMessage);
        setCountErrorSavePost(countErrorSavePost + 1);
      }else {
        handleChangeError('An unknown error occurred');
      }
      setProcessUsername('CUSTOM_ERROR');
    } finally {
      setLoading(false);
    }
  },[username, campaignId, missionId, nameSocialMedia, countErrorSavePost, handleChangeError, determineTypeInput]);

  
  const handleConfirm = useCallback(async () => {
    try {
      setError('');
      setConfirm(false);
      setLoading(true);
      const body: ConfirmUrlPostBody = {
        campaign_id: campaignId, 
        mission_id: missionId,
        media: String(nameSocialMedia).toLowerCase(),
        url: postSaved
      }
      await confirmUrlPost(body);
      await getProgress();
      amplitudeAddTrack('Save Post', {
        social_media: nameSocialMedia,
        timestamp: new Date().toISOString(),
      });
      setConfirm(true);
    } catch (error) {
      if (error instanceof Error) {
        const errorMessage = getError(error);
        handleChangeError(errorMessage);
      }else {
        handleChangeError('An unknown error occurred');
      }
      setProcessUsername('CUSTOM_ERROR');
    } finally {
      setLoading(false);
    }
  }, [campaignId, missionId, nameSocialMedia, postSaved, getProgress, handleChangeError]);

  const handleCancelConfirm = useCallback(()=>{
    setError('');
    setConfirm(false);
    setIsSaveUsername(false);
    setIsSavePost(false);
    setPostSaved('');
    setProcessUsername('SEARCH_POST');
  }, []);
  
  const handleSubmitUrlPost = useCallback(async() =>{
    try {
      setIsSavePost(false);
      setError('');
      setLoading(true);
      setProcessUrl('SAVE_URL');
      const body: ValidateParticipantBody = {
        campaign_id: campaignId, 
        mission_id: missionId,
        media: String(nameSocialMedia).toLowerCase(),
        target: {
          type: determineTypeInput(urlPost),
          value: urlPost
        }
      }
      const res = await validateParticipation?.(body);
      const { user_posts = [] } = res || {};
      if(Array.isArray(user_posts) && user_posts?.length === 0 || !user_posts){
        throw new Error('No se encontro ningun post');
      }
      setPostSaved(Array.isArray(user_posts) && user_posts?.length > 0 ? user_posts[0] : '');
      amplitudeAddTrack('Save Post', {
        social_media: nameSocialMedia,
        timestamp: new Date().toISOString(),
      });
      setIsSavePost(true);
    } catch (error) {
      if (error instanceof Error) {
        const errorMessage = getError(error);
        handleChangeError(errorMessage);
        setCountErrorSavePost(countErrorSavePost + 1);
      }else {
        handleChangeError('An unknown error occurred');
      }
      setProcessUrl('URL_NOT_FOUND');
    } finally {
      setLoading(false);

      if(urlPost){
        await handleConfirm();
      }
    }
  },[urlPost,campaignId, missionId, nameSocialMedia, countErrorSavePost, handleChangeError, handleConfirm, determineTypeInput]);


  const firstRender1 = useRef<boolean>(true);
  useEffect(()=>{
    if (isConnect && !post_url && firstRender1.current) {
      firstRender1.current = false;
      validateUrlPost();
    }
  }, [isConnect, validateUrlPost, post_url])

  useEffect(()=>{
    if (!loadingGetUrlPost && Array.isArray(data) && data?.length > 0 && data[0]) {
      setPostSaved(data[0] ?? '');
    }
  }, [loadingGetUrlPost, data])

  const value = useMemo(()=>({
    isSaveUsername, 
    username, handleChangeUsername,
    isSavePost, 
    urlPost, handleChangeUrlPost,
    processUsername, handleChangeProcessUsername,
    processUrl, handleChangeProcessUrl, 
    loading,
    error,
    confirm,
    postSaved,
    socialMediaSelected,
    countErrorSavePost,
    handleSubmitUsername, handleSubmitUrlPost,
    handleConfirm, handleCancelConfirm
    
  }), [
    isSaveUsername, 
    username, handleChangeUsername, 
    isSavePost,
    urlPost, handleChangeUrlPost,
    processUsername, handleChangeProcessUsername,
    processUrl, handleChangeProcessUrl, 
    loading,
    error,
    confirm,
    postSaved,
    socialMediaSelected,
    countErrorSavePost,
    handleSubmitUsername, handleSubmitUrlPost,
    handleConfirm, handleCancelConfirm
  ])

  return value as HookObjectivePostType
};

export { useObjectivePost };